import { CheckCircleIcon, WarningCircleIcon, InfoIcon } from '@phosphor-icons/react'
import { ScoopText, ScoopButton } from '../../../../ui'
import { PortalPreview } from '../InvestigationReport/components/PortalPreview/PortalPreview'
import styles from './ReadinessPanel.module.scss'

/**
 * ReadinessPanel — the publish-decision surface for a run report
 * (target-state design §5.3a). One checklist composing the trust signals that
 * used to be scattered and quiet: count reconciliation, degenerate peer
 * lenses, never-probed entities — plus the two publish actions (customer
 * preview, DOCX). A clean run shows three green checks, not silence.
 */
export const ReadinessPanel = ({
    readiness,
    runPath,
    showPortalPreview = false,
    entitiesNoun = 'Entities',
    onGenerateDocx,
    isGeneratingDocx,
}) => {
    if (!readiness) return null
    const { derived, authoritative, countMismatch, unprobedCount, lensPairs } = readiness

    const countsLine = countMismatch ? (
        <span className={styles.signal}>
            <WarningCircleIcon size={16} weight="fill" className={styles.warnIcon} />
            <ScoopText className={styles.signalText}>
                The engine reports {authoritative.scanned ?? '—'} scanned /{' '}
                {authoritative.flagged ?? '—'} flagged, but the entity list has{' '}
                {derived.scanned} / {derived.flagged} — the list below may be incomplete.
            </ScoopText>
        </span>
    ) : authoritative ? (
        <span className={styles.signal}>
            <CheckCircleIcon size={16} weight="fill" className={styles.okIcon} />
            <ScoopText className={styles.signalText}>
                Counts reconcile with engine statistics ({derived.scanned} scanned /{' '}
                {derived.flagged} flagged)
            </ScoopText>
        </span>
    ) : (
        <span className={styles.signal}>
            <InfoIcon size={16} className={styles.infoIcon} />
            <ScoopText className={styles.signalText}>
                No engine statistics on this run (legacy artifact) — counts are derived from
                the entity list
            </ScoopText>
        </span>
    )

    const lensesLine =
        lensPairs.length === 0 ? (
            <span className={styles.signal}>
                <CheckCircleIcon size={16} weight="fill" className={styles.okIcon} />
                <ScoopText className={styles.signalText}>
                    Peer lenses resolve to distinct peer sets
                </ScoopText>
            </span>
        ) : (
            <span className={styles.signal}>
                <WarningCircleIcon size={16} weight="fill" className={styles.warnIcon} />
                <ScoopText className={styles.signalText}>
                    {lensPairs
                        .map(([a, b]) => `"${a}" and "${b}" lenses resolve to the same peer set`)
                        .join('; ')}{' '}
                    — the second lens adds no signal
                </ScoopText>
            </span>
        )

    const probedLine =
        unprobedCount === 0 ? (
            <span className={styles.signal}>
                <CheckCircleIcon size={16} weight="fill" className={styles.okIcon} />
                <ScoopText className={styles.signalText}>
                    Every entity in scope was probed
                </ScoopText>
            </span>
        ) : (
            <span className={styles.signal}>
                <WarningCircleIcon size={16} weight="fill" className={styles.warnIcon} />
                <ScoopText className={styles.signalText}>
                    {unprobedCount} {unprobedCount === 1 ? 'entity was' : 'entities were'} never
                    probed — {unprobedCount === 1 ? 'it reads' : 'they read'} as healthy below
                </ScoopText>
            </span>
        )

    return (
        <div className={styles.panel}>
            <div className={styles.headerRow}>
                <ScoopText className={styles.title}>Publish readiness</ScoopText>
                <div className={styles.actions}>
                    {showPortalPreview && runPath && (
                        <PortalPreview runPath={runPath} entitiesNoun={entitiesNoun} />
                    )}
                    {onGenerateDocx && (
                        <ScoopButton
                            variant="grey"
                            size="sm"
                            onClick={onGenerateDocx}
                            loading={isGeneratingDocx}
                        >
                            Download DOCX
                        </ScoopButton>
                    )}
                </div>
            </div>
            <div className={styles.signals}>
                {countsLine}
                {lensesLine}
                {probedLine}
            </div>
        </div>
    )
}
